import { Component, effect, inject } from '@angular/core';
import {
  MenuItem,
  MessageService,
  SharedModule,
  TreeDragDropService,
  TreeNode,
} from 'primeng/api';
import {
  TreeModule,
  TreeNodeContextMenuSelectEvent,
  TreeNodeDropEvent,
} from 'primeng/tree';
import { NgClass, NgIf } from '@angular/common';
import { IndicatorTreeNode } from '../../../models/IndicatorTreeNode';
import { CategoryStore } from '../../../stores/category/category.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { Indicator } from '../../../models/indicator';
import { getState } from '@ngrx/signals';
import { ContextMenuModule } from 'primeng/contextmenu';
import { GlobalsStore } from '../../../stores/globals.state';
import { filter, startWith } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseListComponent } from '../base-list/base-list.component';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { RoutingService } from '../../../services/routing.service';
import { PermissionService } from '../../../services/permission.service';
import { Category } from '../../../models/category';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { IndicatorTreeHelperService } from '../../../indicator-tree-helper.service';

@Component({
  selector: 'app-indicator-tree',
  standalone: true,
  imports: [
    SharedModule,
    TreeModule,
    NgClass,
    NgIf,
    ContextMenuModule,
    HeaderComponent,
    MaskComponent,
    SidebarModule,
    AlertComponent,
  ],
  templateUrl: './indicator-tree.component.html',
  styleUrl: './indicator-tree.component.scss',
  providers: [TreeDragDropService],
})
export class IndicatorTreeComponent extends BaseListComponent<Indicator> {
  readonly store = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly indicatorStore = inject(IndicatorStore);

  selectedIndicator?: Indicator;
  selectedCategory?: Category;

  localStorage: Storage | undefined;
  cachedExpandedStates: { [key: string]: boolean };
  storeMapper: { [key: string]: any } = {
    indicator: this.store,
    category: this.categoryStore,
  };

  selectedNode: any;
  initialized = false;
  groupedIndicators: Record<string, Indicator[]> = {};
  categoryTree: IndicatorTreeNode[] = [];
  items!: MenuItem[];
  categoryContextMenuItems!: MenuItem[];
  indicatorContextMenuItems!: MenuItem[];

  override sidebarId = 'indicator-tree';

  constructor(
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private permissionService: PermissionService,
    private treeHelper: IndicatorTreeHelperService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
    // inject local storage
    this.localStorage = document.defaultView?.localStorage;
    this.cachedExpandedStates = JSON.parse(
      this.localStorage?.getItem('treeExpandedState') || '{}'
    );

    effect(() => {
      const indicatorState = getState(this.store);
      const categoryState = getState(this.categoryStore);
      const globalsState = getState(this.globalsStore);

      if (
        (!this.initialized &&
          !globalsState.indicatorTree.length &&
          indicatorState.initialized &&
          categoryState.initialized) ||
        (indicatorState.trigger &&
          indicatorState.trigger.length &&
          indicatorState.trigger.indexOf('rebuildTree') > -1) ||
        (categoryState.trigger &&
          categoryState.trigger.length &&
          categoryState.trigger.indexOf('rebuildTree') > -1)
      ) {
        this.rerenderTree();
      } else if (globalsState.indicatorTree.length) {
        // todo - tree changes not detected
        this.categoryTree = globalsState.indicatorTree;
      }
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith('initial trigger')
      )
      .subscribe(() => {
        // todo indicator-child form
      });
  }

  /**
   * Tracks and returns the unique identifier for a given tree node.
   *
   * @param {number} index - The index of the node in the collection.
   * @param {IndicatorTreeNode} node - The tree node to track, containing the unique identifier.
   * @return {string} The unique identifier of the given tree node.
   */
  trackByNodeId(index: number, node: IndicatorTreeNode): string {
    return node._id;
  }

  /**
   * Rerenders the category tree by merging indicators with the initial category tree,
   * and updates the global state to reflect the initialized tree.
   *
   * @return {void} Does not return a value.
   */
  rerenderTree(): void {
    this.groupedIndicators = this.store.groupedByCategory();
    const initialCategoryTree = this.categoryStore.asTree();

    if (initialCategoryTree && initialCategoryTree.length) {
      this.initialized = true;

      setTimeout(() => {
        const mergedTree = this.mergeIndicatorsWithCategoryTree(
          initialCategoryTree,
          this.groupedIndicators
        );

        this.treeHelper.applyNumbering(mergedTree);
        this.categoryTree = mergedTree;

        this.globalsStore.setTreeInitialized(this.categoryTree);
      });
    }
  }

  /**
   * Handles the selection of a node in the indicator tree.
   * Updates the selected indicator in the global store and navigates to the root route.
   *
   * @param {IndicatorTreeNode} node - The tree node representing the indicator to be selected.
   * @return {void} This method does not return a value.
   */
  nodeSelect(node: IndicatorTreeNode) {
    this.globalsStore.setSelectedIndicator(
      this.indicatorStore.getById(node._id)
    );
    this.routingService.navigateToRoot();
  }

  /**
   * Toggles the expanded state of a given tree node. If the node has a key,
   * it updates the tree's expanded state accordingly.
   *
   * @param {TreeNode} node - The tree node whose expanded state is to be toggled.
   * @return {void} Does not return a value.
   */
  toggleExpand(node: TreeNode): void {
    node.expanded = !node.expanded;
    if (node.key) this.updateTreeExpandedState(node.key, node.expanded);
  }

  /**
   * Updates the expanded state for a specific tree node and persists the state in local storage.
   *
   * @param {string} key - The unique identifier for the tree node.
   * @param {boolean} isExpanded - The expanded state to set for the specified tree node.
   * @return {void} Does not return a value.
   */
  updateTreeExpandedState(key: string, isExpanded: boolean): void {
    const currentStates = JSON.parse(
      this.localStorage?.getItem('treeExpandedState') || '{}'
    );
    currentStates[key] = isExpanded;
    this.localStorage?.setItem(
      'treeExpandedState',
      JSON.stringify(currentStates)
    );
  }

  /**
   * Determines whether the drop event occurred inside the allowed container.
   *
   * @param {TreeNodeDropEvent} event - The event triggered on a drop action, containing details such as the original event and target element.
   * @return {boolean} Returns true if the drop event occurred inside the allowed container, otherwise false.
   */
  isDroppedInside(event: TreeNodeDropEvent): boolean {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return event.originalEvent.target?.localName !== 'li';
  }

  /**
   * Validates the drop operation for a tree node, determining if the dragged node can be dropped
   * in the target location based on its type and relationship with the target node.
   *
   * @param {TreeNodeDropEvent} event - The event object containing details of the drag and drop operation,
   *                                     including the dragged node and the target drop location.
   * @return {boolean} - Returns true if the drop operation is valid, false otherwise.
   */
  validateDrop(event: TreeNodeDropEvent): boolean {
    const droppedInside = this.isDroppedInside(event);
    const droppedIn = droppedInside ? event.dropNode : event.dropNode?.parent;
    return (
      event.dragNode?.type === 'category' ||
      (event.dragNode?.type !== 'category' && !!droppedIn)
    );
  }

  /**
   * Handles the event when a node is dropped in the tree structure.
   * Validates the drop operation and processes it if accepted.
   *
   * @param {TreeNodeDropEvent} event - The event object containing details about the drop operation.
   * @return {void} Does not return a value.
   */
  nodeDropped(event: TreeNodeDropEvent): void {
    if (event.accept && this.validateDrop(event)) {
      event.accept();
      this.handleDropEvent(event);
    } else {
      console.log('dismiss drop');
    }
  }

  getParent(event: TreeNodeDropEvent) {
    let parent: TreeNode | null = null;
    if (event.dragNode && event.dropNode) {
      if (event.dropNode.children?.includes(event.dragNode)) {
        parent = event.dropNode;
      } else if (
        event.dragNode.parent &&
        event.dragNode.parent.children?.includes(event.dragNode)
      ) {
        parent = event.dragNode?.parent;
      } else if (event.dropNode.type === 'indicator' && event.dropNode.parent) {
        parent = event.dropNode.parent;
      }
    }
    return parent;
  }

  /**
   * Handles the drop event in a tree-like structure by updating the sorting index
   * and parent references of the dragged node and its siblings, and synchronizing
   * updates with the corresponding data store.
   *
   * @param {TreeNodeDropEvent} event - The event object containing details about
   * the drag-and-drop operation, including the dragged node and drop location.
   * @return {void} This method does not return a value.
   */
  handleDropEvent(event: TreeNodeDropEvent): void {
    const parentNode = this.getParent(event);
    const childrenToUpdate: Array<any> = [];
    const dragNode = event.dragNode;
    const dragNodeType = dragNode?.type;
    const dropInside = this.isDroppedInside(event);
    const parentPropertyName =
      dragNodeType === 'indicator' ? 'category' : 'parent';

    // get children to update sorting-index
    const children = (
      parentNode && parentNode.children
        ? parentNode.children
        : this.categoryTree
    ).filter((child: TreeNode) => {
      return child.type === dragNodeType;
    });

    // move dragged element to index 0 if dropped inside new parent
    if (dragNode && dropInside && children && children.length) {
      const dnIndex = children.indexOf(dragNode);
      const [item] = children.splice(dnIndex, 1);
      children.unshift(item);
    }

    // update sorting-index and parent
    if (dragNodeType && children && children.length) {
      children.forEach((child, index) => {
        if (child.key && child.type) {
          const currentChild = this.storeMapper[child.type].getById(child.key);
          const childToUpdate = {
            ...{
              ...currentChild,
              sorting: index,
              [parentPropertyName]: parentNode?.key || undefined,
            },
          };
          childrenToUpdate.push(childToUpdate);
        }
      });

      // update store
      this.storeMapper[dragNodeType].updateMultiple({
        items: childrenToUpdate,
      });
    }
  }

  /**
   * Retrieves and updates the context menu items based on the type of the selected node.
   *
   * @param {TreeNodeContextMenuSelectEvent} event - The event object triggered when a context menu is accessed for a tree node.
   * @return {void} This method does not return a value but updates the context menu items.
   */
  getMenuItemsByType(event: TreeNodeContextMenuSelectEvent): void {
    this.updateContextMenus(this.selectedNode[0].locked);
    if (event.node.type === 'category') {
      this.items = this.categoryContextMenuItems;
    }

    if (event.node.type !== 'category') {
      this.items = this.indicatorContextMenuItems;
    }

    if (!this.hasVisibleItems()) {
      this.items = [
        {
          label: 'keine Zugriffsrechte',
          icon: 'fas fa-info-circle',
        },
      ];
    }
  }

  /**
   * Transforms an Indicator object into a hierarchical tree node structure.
   *
   * @param {Indicator} indicator - The Indicator object to be transformed.
   * @return {IndicatorTreeNode} - A hierarchical node representation of the Indicator object.
   */
  transformIndicatorToHierarchicalItem(
    indicator: Indicator
  ): IndicatorTreeNode {
    const groupedChildren = this.indicatorStore.groupedChildren();
    const children = (groupedChildren[indicator._id!] || []).map(child =>
      this.transformIndicatorToHierarchicalItem(child)
    );

    return {
      _id: indicator._id || '',
      key: indicator._id || '',
      label: indicator.name,
      locked: indicator.locked,
      icon: indicator.isSet ? 'fas fa-ruler-combined' : 'fas fa-ruler-vertical',
      type: 'indicator',
      category: indicator.category,
      children: children,
      sorting: indicator.sorting,
      droppable: false,
    };
  }

  /**
   * Merges indicators into a hierarchical category tree structure, ensuring no duplicate children are added,
   * and recursively processes subcategories.
   *
   * @param {IndicatorTreeNode[]} tree - An array of hierarchical category tree nodes.
   * @param {Record<string, Indicator[]>} groupedIndicators - An object where keys are node IDs and values are arrays of indicators
   *                                                           grouped by their parent node or category.
   * @return {IndicatorTreeNode[]} The modified category tree with indicators merged into their appropriate positions.
   */
  mergeIndicatorsWithCategoryTree(
    tree: IndicatorTreeNode[],
    groupedIndicators: Record<string, Indicator[]>
  ) {
    return tree.map(node => {
      const newNode = { ...node }; // Shallow copy of the node

      if (groupedIndicators[node._id]) {
        const indicatorChildren = groupedIndicators[node._id]
          .filter(indicator => {
            return !indicator.parent; // ignore indicator-sets
          })
          .map(indicator => this.transformIndicatorToHierarchicalItem(indicator))
          .sort((a, b) => a.sorting - b.sorting);
        // Check if the indicatorChildren already exist in node.children
        if (!newNode.children) {
          newNode.children = []; // Initialize children array if it's empty
        }

        const existingIds = new Set(newNode.children.map(child => child._id));
        const nonDuplicateIndicators = indicatorChildren.filter(
          child => !existingIds.has(child._id)
        );

        // Add only non-duplicate children
        newNode.children = [...newNode.children, ...nonDuplicateIndicators];
      }

      // Recursively merge indicators for subcategories
      if (newNode.children.length > 0) {
        newNode.children = this.mergeIndicatorsWithCategoryTree(
          newNode.children,
          groupedIndicators
        );
        newNode.expanded = this.cachedExpandedStates[newNode.key] || false;
      }
      return newNode; // Return the newly constructed node
    });
  }

  /**
   * Updates the context menu options based on the selected node type (category or indicator)
   * and its properties like permissions and lock status. The method dynamically adjusts
   * the available actions in the context menu for categories and indicators.
   *
   * @param {boolean} isLocked - Indicates whether the selected node is currently locked.
   * Determines the visibility of lock/unlock actions in the context menu.
   * @return {void} - This method does not return any value.
   */
  updateContextMenus(isLocked: boolean): void {
    const node = this.selectedNode[0];

    if (node.type === 'category') {
      this.selectedCategory = this.categoryStore.getById(node.key);
      this.categoryContextMenuItems = [
        {
          label: 'bearbeiten',
          icon: 'fas fa-pen',
          visible: this.permissionService.checkIndicatorRights(
            node._id,
            'edit'
          ),
          command: event => {
            //this.editFormOverlay = true;
            this.router.navigate(
              [
                {
                  outlets: {
                    bearbeiten: ['bereich', node._id],
                  },
                },
              ],
              { relativeTo: this.route }
            );
          },
        },
        {
          label: 'Indikator hinzufügen',
          icon: 'fas fa-circle-plus',
          visible: this.permissionService.checkIndicatorRights(node._id, 'add'),
          command: event => {
            this.router.navigate(
              [
                {
                  outlets: {
                    bearbeiten: ['indikator', 'neu', 'general'],
                  },
                },
              ],
              {
                relativeTo: this.route,
                queryParams: { categoryId: node._id || '' },
              }
            );
          },
        },
        {
          label: 'Ordner hinzufügen',
          icon: 'fas fa-circle-plus',
          visible: this.permissionService.checkIndicatorRights(node._id, 'add'),
          command: event => {
            this.router.navigate(
              [
                {
                  outlets: {
                    bearbeiten: ['bereich', 'neu'],
                  },
                },
              ],
              {
                relativeTo: this.route,
                queryParams: { parentId: node._id || '' },
              }
            );
          },
        },
        {
          label: 'sperren',
          icon: 'fas fa-lock',
          visible:
            this.permissionService.checkIndicatorRights(node._id, 'lock') &&
            !isLocked,
          command: event => {
            this.categoryStore.updateLockStatus({
              ids: [node.key],
              isLocked: true,
            });
          },
        },
        {
          label: 'entsperren',
          icon: 'fas fa-unlock',
          visible:
            this.permissionService.checkIndicatorRights(node._id, 'unlock') &&
            isLocked,
          command: event => {
            this.categoryStore.updateLockStatus({
              ids: [node.key],
              isLocked: false,
            });
          },
        },
        {
          label: 'löschen',
          icon: 'fas fa-trash',
          visible: this.permissionService.checkIndicatorRights(
            node._id,
            'remove'
          ),
          command: (event: any) => {
            if (!this.selectedCategory) return;
            this.categoryStore.singleSelection(this.selectedCategory._id);
            setTimeout(() => {
              if (!this.selectedCategory) return;
              this.confirmationDialogService.confirmCategoryDeletion(
                event,
                this.selectedCategory,
                () => {
                  if (this.selectedCategory) {
                    this.categoryStore.deleteItem(this.selectedCategory);
                  }
                }
              );
            });
          },
        },
      ];
    } else {
      this.selectedIndicator = this.indicatorStore.getById(node.key);
      this.indicatorContextMenuItems = [
        {
          label: 'bearbeiten',
          icon: 'fas fa-pen',
          visible: this.permissionService.checkIndicatorRights(
            node.category,
            'edit'
          ),
          command: event => {
            //this.editFormOverlay = true;
            this.router.navigate(
              [
                {
                  outlets: {
                    bearbeiten: ['indikator', node._id, 'general'],
                  },
                },
              ],
              { relativeTo: this.route }
            );
          },
        },
        {
          label: 'sperren',
          icon: 'fas fa-lock',
          visible:
            this.permissionService.checkIndicatorRights(
              node.category,
              'lock'
            ) && !isLocked,
          command: event => {
            this.store.updateLockStatus({
              ids: [node.key],
              isLocked: true,
            });
          },
        },
        {
          label: 'entsperren',
          icon: 'fas fa-unlock',
          visible:
            this.permissionService.checkIndicatorRights(
              node.category,
              'unlock'
            ) && isLocked,
          command: event => {
            this.store.updateLockStatus({
              ids: [node.key],
              isLocked: false,
            });
          },
        },
        {
          label: 'löschen',
          icon: 'fas fa-trash',
          visible: this.permissionService.checkIndicatorRights(
            node.category,
            'remove'
          ),
          command: (event: any) => {
            if (!this.selectedIndicator) return;
            this.store.singleSelection(this.selectedIndicator._id);
            setTimeout(() => {
              if (!this.selectedIndicator) return;
              this.confirmationDialogService.confirmIndicatorDeletion(
                event,
                this.selectedIndicator,
                () => {
                  if (this.selectedIndicator) {
                    this.indicatorStore.deleteItem(
                      this.selectedIndicator as Indicator
                    );
                  }
                }
              );
            });
          },
        },
      ];
    }
  }

  /**
   * Checks if there are any visible items in the collection of items.
   *
   * @return {number} The number of visible items in the collection.
   */
  hasVisibleItems(): number {
    return this.items.filter((item: MenuItem) => {
      return item.visible;
    }).length;
  }
}
